import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import StyledButton from '../common/StyledButton';

interface NavItem {
  id: string;
  label: string;
}

const navItems: NavItem[] = [
  { id: 'accueil', label: 'Accueil' },
  { id: 'a-propos', label: 'À Propos' },
  { id: 'services', label: 'Services' },
  { id: 'avis', label: 'Avis' },
  { id: 'contact', label: 'Contact' },
];

const NavbarContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: ${(props) => (props.$isScrolled ? '0.5rem 0' : '1rem 0')};
  background-color: ${(props) =>
    props.$isScrolled ? 'var(--primary-color)' : 'transparent'};
  transition: all var(--transition-medium);
  box-shadow: ${(props) =>
    props.$isScrolled ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-sm);
`;

const Logo = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--accent);
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-light);
  font-weight: 500;
  padding: 0.5rem 1rem;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 50%;
    background-color: var(--accent);
    transition: all var(--transition-fast);
    transform: translateX(-50%);
  }
  
  &:hover:after,
  &.active:after {
    width: 80%;
  }
`;

const MobileMenuButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 1.5rem;
  cursor: pointer;
  display: none;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--primary-color);
  transform: translateY(${(props) => (props.$isOpen ? '0' : '-100%')});
  transition: transform var(--transition-medium);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: var(--spacing-lg);
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  text-align: center;
`;

const MobileNavLink = styled(Link)`
  color: var(--text-light);
  font-size: 1.5rem;
  font-weight: 500;
  padding: 0.5rem;
  cursor: pointer;
  
  &:hover {
    color: var(--accent);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  color: var(--accent);
  font-size: 1.5rem;
  cursor: pointer;
`;

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <NavbarContainer $isScrolled={isScrolled}>
        <NavContent>
          <Logo>
            Touré VTC
          </Logo>
          
          <NavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.id}
                smooth={true}
                duration={500}
                spy={true}
                activeClass="active"
                offset={-100}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="contact" smooth={true} duration={800} offset={-100}>
              <StyledButton variant="primary" size="small">
                Réserver
              </StyledButton>
            </Link>
          </NavLinks>
          
          <MobileMenuButton onClick={toggleMobileMenu}>
            ☰
          </MobileMenuButton>
        </NavContent>
      </NavbarContainer>
      
      <MobileMenu $isOpen={isMobileMenuOpen}>
        <CloseButton onClick={toggleMobileMenu}>✕</CloseButton>
        <MobileNavLinks>
          {navItems.map((item) => (
            <MobileNavLink
              key={item.id}
              to={item.id}
              smooth={true}
              duration={500}
              spy={true}
              onClick={toggleMobileMenu}
            >
              {item.label}
            </MobileNavLink>
          ))}
          <Link to="contact" smooth={true} duration={800} offset={-100}>
            <StyledButton variant="primary" size="medium" onClick={toggleMobileMenu}>
              Réserver
            </StyledButton>
          </Link>
        </MobileNavLinks>
      </MobileMenu>
    </>
  );
};

export default Navbar; 